import { useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  Flex,
  Input,
  Portal,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import { capitalizeFirstLetter } from '@utils/formats';
import ErrorLabel from '@components/ErrorLabel';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';
import api from '@services/api';

interface Props {
  sourceFunnelId: string;
  targetFunnelId: string;
  onClose: () => void;
  onCreated: (dispositionOptionId: string, label: string) => void;
}

const CreateEdgeDialog = ({ sourceFunnelId, targetFunnelId, onClose, onCreated }: Props) => {
  const [loading, setLoading] = useState(false);
  const [nameError, setNameError] = useState('');

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const name = capitalizeFirstLetter((formData.get('name') as string)?.trim());

    if (!name || name.length < 3) {
      setNameError('O nome deve ter ao menos 3 caracteres.');
      return;
    }

    setNameError('');
    setLoading(true);

    const isTerminalBase = targetFunnelId === 'terminal-base';
    const isTerminalBlocked = targetFunnelId === 'terminal-blocked';
    const behaviour = isTerminalBlocked ? 1 : isTerminalBase ? 0 : 3;

    try {
      const payload: Record<string, unknown> = { name, behaviour };
      if (behaviour === 3) {
        payload.next_funnel_id = Number(targetFunnelId.replace('funnel-', ''));
      }

      const dispositionResponse = await api.post('/disposition_option', payload);

      if (dispositionResponse.status !== 200) {
        toast.error('Erro ao criar tabulação.');
        return;
      }

      const dispositionId = dispositionResponse.data.id;

      await api.post(`/sales_funnel/${sourceFunnelId}/disposition/${dispositionId}`);

      onCreated(dispositionId, name);
      toast.success('Conexão criada!');
    } catch (error) {
      toast.error('Erro ao criar conexão. Tente novamente.');
      datadogRum.addError(new Error('Fail to create funnel edge'), {
        error,
        sourceFunnelId,
        targetFunnelId,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog.Root size="sm" placement="center" open onOpenChange={onClose} closeOnInteractOutside={false}>
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Nova tabulação de avanço</Dialog.Title>
            </Dialog.Header>

            <form onSubmit={onSubmit}>
              <Dialog.Body>
                <Field.Root invalid={!!nameError}>
                  <Field.Label>Nome da tabulação</Field.Label>
                  <Input
                    name="name"
                    placeholder="Ex: Interesse identificado"
                    variant="subtle"
                    minLength={3}
                    required
                    autoFocus
                  />
                  {nameError && <ErrorLabel text={nameError} />}
                </Field.Root>
              </Dialog.Body>

              <Dialog.Footer>
                <Dialog.ActionTrigger asChild>
                  <Button variant="outline" disabled={loading}>Cancelar</Button>
                </Dialog.ActionTrigger>
                <Button bg="green.700" color="white" type="submit" loading={loading}>
                  Criar
                </Button>
              </Dialog.Footer>
            </form>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default CreateEdgeDialog;
